import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import DiscoverIcon from "icons/payments/Discover";
import { MastercardIcon } from "icons/payments/MastercardIcon";
import UnionPay from "icons/payments/UnionPay";
import { VisaIcon } from "icons/payments/VisaIcon";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { countryAndState } from "store/slices/documentsSlice";
import {
  countryListGet,
  useCountryList,
} from "store/slices/paymentMethodSlice";
import {
  CardFormCheckType,
  CountryOption,
  PhoneInput,
  StateOption,
} from "types/common";
import { valuesProps } from "types/paymentMethods";
import { AddressDetails, countryAddressList } from "utils/countryAddressList";
import {
  checkAddress,
  checkOverPaymentAmount,
  checkPhone,
  checkZip,
  decode,
  detectCountry,
  formatCurrency,
  formatMinAndMaxAmount,
  validateEmail,
} from "utils/HelperFunctions";

import JCB from "icons/payments/JCB.svg";
import amex from "icons/payments/amex.svg";
import dinersClub from "icons/payments/dinersClub.svg";
import InputField from "components/form/InputField";
import { AddressPayload, SettingsValue } from "types/payments";
import InputSelect from "components/form/InputSelect";
import { addNotification, useUserData } from "store/slices/authSlice";
import Button from "components/form/Button";
import {
  getCountryCallingCode,
  isValidPhoneNumber,
} from "react-phone-number-input";
import { countryCodes } from "pages/profile/countrycode";
import { useParams } from "react-router-dom";
import {
  duplicatePaymentGet,
  getIntent,
  markIncompletePayment,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  storePaymnetPlan,
  setIsPaymentProcessing,
  storeCardValues,
} from "store/slices/paymentsSlice";
import { StripeCardNumberElement } from "@stripe/stripe-js";
import { CountryCode } from "types/UserProfile";
import { RootState } from "store/store";
import MobileInputNew from "components/form/MobileInputNew";
import { ConvenienceFeeDisplay } from "./PaymentMethodComponents";
import { captureException } from "utils/sentry";
import { getPaymentTotalWithFee } from "utils/paymentFee";
import {
  getQuickCheckoutFeeState,
  isQuickCheckoutRoute,
} from "utils/quickCheckout";
import {
  getClientSecret,
  getFailedIntentId,
  PAYMENT_CONFIRMATION_ERROR,
  PAYMENT_START_ERROR,
  setStoredCardElement,
} from "utils/stripeIntent";
import {
  getIntentOrderId,
  getPaymentFailureReason,
} from "utils/incompleteOrder";
import { arrayBuffer } from "stream/consumers";

interface StripeCardPaymentCheckoutFormProps {
  handleChange: (e: any, value: string) => void;
  isEdit?: boolean | undefined;
  values: valuesProps;
  iconType: string;
  selectedState: any;
  setSelectedState: any;
  cardErrMsg?: string | "";
  setEmail?: any;
  email?: string;
  setShowPreview?: any;
  isPaymentLink?: boolean;
  amount?: any;
  isRecurring?: boolean;
  paymentPlan?: any;
  isAgreement?: boolean;
  handleBack?: any;
  isUpdatePlan?: boolean;
  isSchedulePayment?: boolean;
  isRetry?: boolean;
  isUpdateSubscription?: boolean;
  checkNegativeBalance?: boolean;
  isProductLink?: boolean;
  accounts_id?: string | null;
  setIconType: (val: string) => void;
  collectAddress?: boolean;
  collectPhone?: boolean;
  phoneNumber?: PhoneInput;
  setPhoneNumber?: React.Dispatch<React.SetStateAction<PhoneInput>>;
  handleRetryNext?: () => void;
  isUpdateSubscriptionPaymentMethod?: boolean;
  isDowngradePlan?: boolean;
  showConvFee?: boolean;
  description?: string | null;
  isInvoice?: boolean;
  verifyStripeCardPayment?: ({
    intent,
    email,
    error,
    orderId,
    principalAmount,
    failedIntent,
  }: {
    intent?: string | null;
    email?: string;
    error?: any;
    orderId?: number;
    principalAmount?: number;
    failedIntent?: string | null;
  }) => Promise<void>;

  formCheck: CardFormCheckType;
  setFormCheck: React.Dispatch<React.SetStateAction<CardFormCheckType>>;
  formEmptyCheck: CardFormCheckType;
  setFormEmptyCheck: React.Dispatch<React.SetStateAction<CardFormCheckType>>;
  handleEmailBlur?: () => void;
}

type StripeCardEvent = {
  elementType: string;
};

type CardBrand =
  | "visa"
  | "amex"
  | "mc"
  | "mastercard"
  | "discover"
  | "ds"
  | "jcb"
  | "diners"
  | "dinersclub"
  | "diners club"
  | "dc"
  | "unionpay"
  | string;

let globalStripeInstance: any = null;
let globalCardElement: any = null;

const StripeCardPaymentCheckoutForm = forwardRef(
  (
    {
      handleChange,
      isEdit,
      values,
      isInvoice = false,
      iconType,
      selectedState,
      setSelectedState,
      setEmail,
      handleEmailBlur = () => {},
      cardErrMsg = "",
      email,
      setShowPreview,
      isPaymentLink = true,
      amount,
      isRecurring = false,
      paymentPlan,
      isAgreement = false,
      handleBack = () => {},
      isUpdatePlan = false,
      isSchedulePayment = false,
      isRetry = false,
      isUpdateSubscription = false,
      checkNegativeBalance = false,
      isProductLink = false,
      accounts_id = "",
      setIconType,
      collectAddress = true,
      collectPhone = false,
      isDowngradePlan = false,
      verifyStripeCardPayment = async () => {},
      phoneNumber = {
        country_code: "",
        phone: "",
        postal_code: "",
      },
      setPhoneNumber = () => {},
      isUpdateSubscriptionPaymentMethod = false,
      description = "Online Payment",
      showConvFee = false,
      formCheck,
      setFormCheck,
      formEmptyCheck,
      setFormEmptyCheck,
      handleRetryNext = () => {},
    }: StripeCardPaymentCheckoutFormProps,
    ref
  ) => {
    const portalSettings = usePortalSettingsData();
    const countryList: CountryOption[] = useCountryList();
    const showFinalDisclosure = useShowFinalDisclosure();
    const userData = useUserData();
    const {
      conFee,
      isFeeLoading,
      generalSettings,
      checkedDuplicatePayment,
      cardValues,
    } = useAppSelector((state: RootState) => state.payment);

    const { isChecking, isSubscriptionAllowed } = useAppSelector(
      (state: RootState) => state.subscription
    );

    const { checkAmount } = useAppSelector((state: RootState) => state.common);
    const { clusterId: cluster_id, id: actionId } = useParams();
    const clusterId = decode(cluster_id || "", true);
    const resolvedClusterId =
      clusterId ||
      (actionId ? decode(actionId || "")?.split("_")[1] : undefined) ||
      userData?.data?.accountData?.clusterid;
    const [cardError, setCardError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const [isErr, setIsErr] = useState<string | null>(null);
    const stripe = useStripe();
    const elements = useElements();
    const elementOptions: Record<string, any> = {
      style: {
        base: {
          fontSize: "12px",
          color: "#19224c",
          "::placeholder": {
            color: "#8c90a5",
          },
          ":hover": {},
        },
        invalid: {
          color: "#ff0000",
        },
      },
    };
    const cardNumberElementOptions: Record<string, any> = {
      ...elementOptions,
      disableLink: true,
    };

    const dispatch = useAppDispatch();
    const markIntentFailed = async (orderId: number | undefined, error: any) => {
      if (!orderId) return;
      try {
        await dispatch(
          markIncompletePayment({
            orderId,
            cluster_id: resolvedClusterId || undefined,
            failed_reason: getPaymentFailureReason(error),
          })
        ).unwrap();
      } catch (markError) {
        captureException(markError, {
          tags: { payment_flow: "mark_incomplete_failed" },
          extra: { order_id: orderId },
        });
      }
    };
    const [fieldArr, setFieldArr] = useState<string[] | undefined>();
    const [fieldObj, setFieldObj] = useState<AddressDetails | undefined>();

    const onCountryListGet = async (): Promise<void> => {
      try {
        await dispatch(countryListGet()).unwrap();
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.log("conntry list get err: -->", error?.message);
      }
    };

    const [states, setStates] = useState<StateOption[]>([]);
    const [countriesWithStates, setCountriesWithStates] = useState<any[]>([]);
    const initCountryRef = useRef<boolean>(false);
    const autoDetectRef = useRef<boolean>(false);

    useEffect(() => {
      autoDetectRef.current = false;
    }, []);

    const setCountry = async (): Promise<void> => {
      const userCountry = await detectCountry();
      if (userCountry) handleChange({ value: userCountry }, "x_country");
    };

    useEffect(() => {
      if (countryList.length === 0) {
        onCountryListGet();
        return;
      }
      if (
        !isEdit &&
        !autoDetectRef.current &&
        (!values?.x_country || values?.x_country === "US")
      ) {
        setCountry();
        autoDetectRef.current = true;
      }
    }, [countryList]);

    useEffect(() => {
      if (values?.x_country) {
        let data = countryAddressList()[values?.x_country || "US"];
        if (data) {
          const arr = Object.keys(data).map((field) => field);
          const obj = countryAddressList()[values?.x_country || "US"];
          setFieldArr(arr);
          setFieldObj(obj);
        }
      }
      if (isEdit && values?.x_country) {
        const nStates = getStatesForCountryCode(values?.x_country);
        setStates(nStates);
        if (values?.x_state) {
          const selectedStateObject: any = nStates.find(
            (state: any) => state.name === values?.x_state
          );
          if (selectedStateObject) {
            setSelectedState(selectedStateObject);
            handleChange(selectedStateObject?.name, "x_state");
          }
        }
      } else if (isEdit && !values?.x_country) {
        setCountry();
        autoDetectRef.current = true;
      }
    }, [isEdit, values?.x_country]);

    useEffect(() => {
      if (countryList?.length > 0) {
        setCountry();
        autoDetectRef.current = true;
      }
    }, [countryList]);

    const getPostalCode = (): string | false => {
      if (fieldArr && fieldArr.includes("CEDEX")) return "CEDEX";
      if (fieldArr && fieldArr.includes("ZIP")) return "ZIP";
      if (fieldArr && fieldArr.includes("Eircode")) return "Eircode";
      if (fieldArr && fieldArr.includes("PIN")) return "PIN";
      if (fieldArr && fieldArr.includes("Postal code")) return "Postal code";
      return false;
    };

    const getStateName = (): string | false => {
      if (fieldArr && fieldArr.includes("County")) return "County";
      if (fieldArr && fieldArr.includes("State")) return "State";
      if (fieldArr && fieldArr.includes("Department")) return "Department";
      if (fieldArr && fieldArr.includes("District")) return "District";
      if (fieldArr && fieldArr.includes("Emirate")) return "Emirate";
      if (fieldArr && fieldArr.includes("Island")) return "Island";
      if (fieldArr && fieldArr.includes("Oblast")) return "Oblast";
      if (fieldArr && fieldArr.includes("Parish")) return "Parish";
      if (fieldArr && fieldArr.includes("Prefecture")) return "Prefecture";
      if (fieldArr && fieldArr.includes("Province")) return "Province";
      return false;
    };
    const selectedCountryUI = useMemo(
      () => values?.x_country ?? "",
      [values?.x_country]
    );

    useEffect(() => {
      const getCountryAndState = async () => {
        try {
          const res: any = await dispatch(countryAndState()).unwrap();
          const data = res?.body?.data || [];
          setCountriesWithStates(data);
        } catch (error) {
          console.error(error);
        }
      };

      getCountryAndState();
    }, []);

    const getStatesForCountryCode = (code: string): StateOption[] => {
      try {
        const countryMeta = countryList?.find((c) => c?.value === code);
        const countryName = countryMeta?.name;
        if (!countryName) return [];
        const found = countriesWithStates?.find(
          (c: any) =>
            c?.country_name?.toLowerCase() === countryName?.toLowerCase()
        );
        const st = found?.states || [];
        return st.map((s: { state_name: string; state_code: string }) => ({
          name: s?.state_name,
          value: s?.state_name,
          state_code: s?.state_code,
        }));
      } catch (e) {
        return [];
      }
    };

    useEffect(() => {
      if (
        values?.x_country &&
        countriesWithStates?.length > 0 &&
        countryList?.length > 0
      ) {
        const nStates = getStatesForCountryCode(values?.x_country);
        setStates(nStates);
      }
    }, [values?.x_country, countriesWithStates, countryList]);

    useEffect(() => {
      if (
        isEdit &&
        !initCountryRef.current &&
        selectedCountryUI &&
        countryList?.length > 0
      ) {
        const ev = countryList.find((c: any) => c?.value === selectedCountryUI);
        if (ev) {
          const nStates = getStatesForCountryCode(selectedCountryUI as string);
          setStates(nStates);
          handleChange(ev, "x_country");
          initCountryRef.current = true;
        }
      }
    }, [isEdit, selectedCountryUI, countryList, handleChange]);

    const imgUrl = (): React.ReactNode => {
      const brand: CardBrand = (
        values?.card_type ||
        iconType ||
        ""
      )?.toLowerCase();
      switch (brand) {
        case "visa":
          return <VisaIcon />;
        case "amex":
        case "american express":
        case "americanexpress":
        case "american expres":
          return <img src={amex} alt="Amex" />;
        case "mc":
        case "master":
        case "mastercard":
          return <MastercardIcon />;
        case "discover":
        case "ds":
          return <DiscoverIcon />;
        case "jcb":
          return <img src={JCB} alt="JCB" />;
        case "diners":
        case "dinersclub":
        case "diners club":
        case "dc":
          return <img src={dinersClub} alt="Diners Club" />;
        case "unionpay":
          return <UnionPay />;
        default:
          return null;
      }
    };

    const getCountryShortName = (phoneNumber?: string | number) => {
      try {
        const idx =
          typeof phoneNumber === "string" ? parseInt(phoneNumber) : phoneNumber;
        const parsedPhoneNumber = countryCodes[Number(idx)];
        return parsedPhoneNumber ?? "US";
      } catch (error) {
        console.error("Error parsing phone number:", error);
        return "US";
      }
    };

    const handleOnPhoneChange = useCallback((phone: string) => {
      if (phone?.length > 3) {
        const isCheckValid = isValidPhoneNumber(phone);
        //   setIsValidNumber(isCheckValid);
      } else {
        //   setIsValidNumber(true);
      }
      setPhoneNumber((state) => ({
        ...state,
        phone,
      }));
    }, []);

    const handlePhoneCountryChange = useCallback((itm: string) => {
      setPhoneNumber((state) => ({
        ...state,
        country_code: getCountryCallingCode(itm as CountryCode),
      }));
    }, []);

    const validateStripeCard = (e: StripeCardEvent): void => {
      if (
        e.elementType === "cardNumber" &&
        !formCheck.card &&
        !formEmptyCheck.card
      ) {
        setCardError("Your card's number is incomplete.");
        return;
      }
      if (
        e.elementType === "cardExpiry" &&
        !formCheck.expiry &&
        !formEmptyCheck.expiry
      ) {
        setCardError("Your card's expiration date is incomplete.");
        return;
      }
      if (
        e.elementType === "cardCvc" &&
        !formCheck.cvv &&
        !formEmptyCheck.cvv
      ) {
        setCardError("Your card's security code is incomplete.");
        return;
      }

      if (formCheck.card && formCheck.expiry && formCheck.cvv) {
        setCardError(null);
        return;
      } else {
        setCardError(
          !formCheck.card && !formEmptyCheck.card
            ? "Your card's number is incomplete."
            : !formCheck.expiry && !formEmptyCheck.expiry
              ? "Your card's expiration date is incomplete."
              : !formCheck.cvv && !formEmptyCheck.cvv
                ? "Your card's security code is incomplete."
                : null
        );
        return;
      }
    };

    const isDisabled = useMemo<boolean>(() => {
      // Shown (cc_billing_address) is not the same as required
      // (cc_billing_addr_mandatory): with the address optional, ZIP alone is enough.
      const isAddressRequired = isProductLink
        ? collectAddress
          ? "Y"
          : "N"
        : portalSettings?.cc_billing_addr_mandatory;

      const addressField = checkAddress(
        portalSettings?.cc_billing_address,
        isAddressRequired,
        values,
        collectAddress
      );
      const validZip = checkZip(values);

      const isPhone = collectPhone ? checkPhone(phoneNumber) : true;
      return (
        formCheck.card === false ||
        formCheck.expiry === false ||
        formCheck.cvv === false ||
        !addressField ||
        !values?.choldername ||
        !values?.choldername.trim() ||
        !isPhone ||
        !email ||
        !email.trim() ||
        !validateEmail(email) ||
        !validZip
      );
    }, [formCheck, values, portalSettings, phoneNumber, email]);

    const checkPastAmount = async () => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id ?? accounts_id,
          payment_method: "CC",
          amount: amount,
        };
        let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
        const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
        if (isDuplicateOrders && duplicateOrdersCount > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleCheckDisclosureAndDuplicate = async (
      cardElement: StripeCardNumberElement | null
    ) => {
      try {
        let moveAhead =
          !checkedDuplicatePayment && !isProductLink
            ? await checkPastAmount()
            : true;

        if (!moveAhead) {
          setIsLoading(false);
          dispatch(onSetFinalDisclosureLoading(false));
          setStoredCardElement(cardElement);
          dispatch(storeCardValues(values));
          if (paymentPlan) {
            await dispatch(storePaymnetPlan(paymentPlan));
          }
          return false;
        } else {
          if (
            (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
              portalSettings?.payment_agreement_opt ===
                SettingsValue.DEFAULT) &&
            !showFinalDisclosure
          ) {
            setIsLoading(false);
            dispatch(resetDuplicateOrder());
            dispatch(storeCardValues(values));
            setStoredCardElement(cardElement);
            globalStripeInstance = stripe;
            globalCardElement = cardElement;
            dispatch(onSetFinalDisclosureLoading(false));
            dispatch(onSetShowDisclosure(true));
            if (paymentPlan) {
              await dispatch(storePaymnetPlan(paymentPlan));
            }
          } else {
            dispatch(resetDuplicateOrder());
            dispatch(resetCheckDuplicatePayment());
            return true;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const validateOtherChecks = async (
      cardElement: StripeCardNumberElement | null
    ) => {
      try {
        let negativeBalance =
          !checkAmount && checkNegativeBalance
            ? await checkOverPaymentAmount({
                amount: Number(amount),
                balance: userData?.data?.accountData?.balance,
                overpayment_protection: portalSettings?.overpayment_protection,
                dispatch,
              })
            : true;
        if (!negativeBalance) {
          setIsLoading(false);
          return false;
        }

        let disclosureOrDuplicate =
          await handleCheckDisclosureAndDuplicate(cardElement);
        if (!disclosureOrDuplicate) {
          return false;
        }

        return true;
      } catch (error) {
        console.log("error ======", error);
        return false;
      }
    };

    const createAddressPayload = (): AddressPayload => {
      let payload: AddressPayload = {};
      let dataValues = cardValues ? cardValues : values;
      let data = countryAddressList()[dataValues?.x_country || "US"];
      const arr = Object.keys(data).map((field) => field);
      const hasPostalCodeField = [
        "CEDEX",
        "ZIP",
        "Eircode",
        "PIN",
        "Postal code",
      ].some((field) => arr.includes(field));


      payload = { ...payload, country: dataValues?.x_country || "US" };
      if (hasPostalCodeField) {
        payload = { ...payload, postal_code: dataValues?.x_zip || undefined };
      }
      if (portalSettings?.cc_billing_address === SettingsValue.YES) {
        if (arr) {
          if (arr.includes("Address line 1")) {
            payload = { ...payload, line1: dataValues?.x_address || undefined };
          }
          if (arr.includes("Address line 2")) {
            payload = { ...payload, line2: dataValues?.address2 || undefined };
          }
          if (arr.includes("City") || arr.includes("Area")) {
            payload = { ...payload, city: dataValues?.x_city || undefined };
          }
          if (
            arr.includes("County") ||
            arr.includes("State") ||
            arr.includes("Department") ||
            arr.includes("District") ||
            arr.includes("Emirate") ||
            arr.includes("Island") ||
            arr.includes("Oblast") ||
            arr.includes("Parish") ||
            arr.includes("Prefecture") ||
            arr.includes("Province")
          ) {
            payload = { ...payload, state: dataValues?.x_state || undefined };
          }
        }
      }
      return payload;
    };

    const handleCardSetup = async ({
      clientSecret,
      orderId,
      principalAmount,
    }: {
      clientSecret: string;
      orderId?: number;
      principalAmount: number;
    }) => {
      if (!stripe || !elements) {
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
        return;
      }
      let stripeConfirmationSucceeded = false;
      try {
        const { error, setupIntent } =
          await stripe.confirmCardSetup(clientSecret);

        if (error) {
          await markIntentFailed(orderId, error);
          await verifyStripeCardPayment({
            intent: null,
            email,
            error,
            orderId,
            principalAmount,
          });
          // setIsErr(error.message || null);
          // setIsLoading(false);
          // await dispatch(onSetShowDisclosure(false));
          // await dispatch(onSetFinalDisclosureLoading(false));
          return;
        } else {
          stripeConfirmationSucceeded = true;
          await verifyStripeCardPayment({
            intent: setupIntent?.id || null,
            email,
            error: null,
            orderId,
            principalAmount,
          });
        }
      } catch (error) {
        if (!stripeConfirmationSucceeded) {
          await markIntentFailed(orderId, error);
        }
        await dispatch(onSetShowDisclosure(false));
        await dispatch(onSetFinalDisclosureLoading(false));
        setIsErr(PAYMENT_CONFIRMATION_ERROR);
        captureException(error, {
          tags: { payment_flow: "stripe_card_setup" },
          extra: { is_retry: isRetry },
        });
      }
    };

    const handleCardPayment = async ({
      clientSecret,
      orderId,
      principalAmount,
    }: {
      clientSecret: string;
      orderId?: number;
      principalAmount: number;
    }) => {
      if (!stripe || !elements) {
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
        return;
      }
      let stripeConfirmationSucceeded = false;
      try {
        const { error, paymentIntent } =
          await stripe.confirmCardPayment(clientSecret);
        if (error) {
          await markIntentFailed(orderId, error);
          await verifyStripeCardPayment({
            intent: null,
            email,
            error,
            orderId,
            principalAmount,
            failedIntent: getFailedIntentId(error, clientSecret),
          });
          // setIsErr(error.message || null);
          // await dispatch(onSetShowDisclosure(false));
          // await dispatch(onSetFinalDisclosureLoading(false));
          // setIsLoading(false);
          // return;
        } else {
          stripeConfirmationSucceeded = true;
          await verifyStripeCardPayment({
            intent: paymentIntent?.id || null,
            email,
            error: null,
            orderId,
            principalAmount,
          });
        }
      } catch (error) {
        if (!stripeConfirmationSucceeded) {
          await markIntentFailed(orderId, error);
        }
        setIsErr(PAYMENT_CONFIRMATION_ERROR);
        captureException(error, {
          tags: { payment_flow: "stripe_card_payment" },
          extra: { is_retry: isRetry },
        });
        await dispatch(onSetFinalDisclosureLoading(false));
        await dispatch(onSetShowDisclosure(false));
      }
    };

    const createPaymentMethod = async ({
      cardElement,
      addressData,
    }: {
      cardElement: StripeCardNumberElement | null;
      addressData: AddressPayload;
    }) => {
      if (!stripe || !elements) {
        return {
          success: false,
          error: { message: "Stripe is not ready yet. Please try again." },
        };
      }

      let phoneValue = collectPhone
        ? { phone: phoneNumber?.phone }
        : userData?.data?.accountData?.phone
          ? { phone: userData?.data?.accountData?.phone }
          : {};

      let dataValues = cardValues ? cardValues : values;

      if (!cardElement) {
        return {
          success: false,
          error: {
            message:
              "Card details are not available. Please re-enter card details and try again.",
          },
        };
      }

      const activeStripe =
        showFinalDisclosure && globalStripeInstance
          ? globalStripeInstance
          : stripe;
      const activeCardElement =
        showFinalDisclosure && globalCardElement
          ? globalCardElement
          : cardElement;

      const { paymentMethod, error: pmError } =
        await activeStripe.createPaymentMethod({
          type: "card",
          card: activeCardElement,
          billing_details: {
            name: dataValues.choldername,
            email: email,
            address: { ...addressData },
            ...phoneValue,
          },
        });

      if (pmError) {
        console.log("pmError ======", pmError);
        setIsErr(pmError.message || null);
        return { success: false, error: pmError };
      } else {
        return { success: true, paymentMethod };
      }
    };

    const handleOnSubmit = async () => {
      let currentStripe = stripe;
      let currentCardElement = elements?.getElement(CardNumberElement);

      if (showFinalDisclosure && globalCardElement && globalStripeInstance) {
        currentStripe = globalStripeInstance;
        currentCardElement = globalCardElement;
      }

      if (!currentStripe || !currentCardElement) {
        setIsErr(
          "Card details are not available. Please re-enter card details and try again."
        );
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
        return;
      }

      setIsLoading(true);
      dispatch(setIsPaymentProcessing(true));
      setCardError(null);
      setIsErr(null);

      let checkVerified =
        isUpdateSubscriptionPaymentMethod || isDowngradePlan
          ? true
          : await validateOtherChecks(currentCardElement);

      if (!checkVerified) {
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
        return;
      }

      const addressData = createAddressPayload();
      let paymentMethodResponse = await createPaymentMethod({
        cardElement: currentCardElement,
        addressData,
      });


      if (!paymentMethodResponse?.success) {
        setIsErr(
          paymentMethodResponse?.error?.message ||
            "Unable to create payment method. Please try again."
        );
        await dispatch(onSetShowDisclosure(false));
        await dispatch(onSetFinalDisclosureLoading(false));
        dispatch(setIsPaymentProcessing(false));
        setIsLoading(false);
        return;
      }

      try {
        let clusterid = resolvedClusterId;

        let payment_link_id = null;

        if (actionId?.startsWith("pl_")) {
          const decoded = decode(actionId || "");
          if (decoded) {
            payment_link_id = decoded?.split("_")[0];
          }
        }

        const principalAmount = Number(amount);
        if (
          !Number.isFinite(principalAmount) ||
          (!isRecurring && principalAmount <= 0)
        ) {
          setIsErr("Enter a valid payment amount.");
          dispatch(setIsPaymentProcessing(false));
          setIsLoading(false);
          return;
        }

        const payload = {
          total_amount: Math.round(principalAmount * 100),
          payment_method_types: "card",
          accountid: userData?.data?.accountData?.accountid || "",
          cluster_id: clusterid,
          isRecurring,
          description: "Online Payment",
          payment_note: description,
          payment_method_id: paymentMethodResponse?.paymentMethod?.id || "",
          isAgreement,
          // Lets the server refuse the intent before the card is charged
          // when the agreement could not be signed afterwards.
          ...(isAgreement && actionId ? { slug: actionId } : {}),
          custom_conv: showConvFee ? "on" : "off",
          isProductLink,
          payment_link_id: payment_link_id,
          isRetry,
          order_id:
            isRetry && actionId
              ? Number(decode(actionId || "")?.split("_")?.[0]) || undefined
              : undefined,
          // Quick checkout prices the fee by the billing state's code, the
          // same value the page used for the fee it shows.
          ...(isQuickCheckoutRoute()
            ? {
                state: getQuickCheckoutFeeState(
                  values?.x_country,
                  values?.x_state,
                  selectedState
                ),
              }
            : {}),
        };

        const res: any = await dispatch(getIntent(payload)).unwrap();
        if (res?.body?.success) {
          const clientSecret = getClientSecret(res);

          if (!clientSecret) {
            setIsErr(PAYMENT_START_ERROR);
            captureException(
              new Error("Payment intent response was missing client_secret"),
              {
                tags: { payment_flow: "stripe_card_intent" },
                extra: {
                  is_retry: isRetry,
                  is_recurring: isRecurring,
                  response_success: true,
                },
              }
            );
            await dispatch(onSetShowDisclosure(false));
            await dispatch(onSetFinalDisclosureLoading(false));
            dispatch(setIsPaymentProcessing(false));
            return;
          }

          if (isInvoice) {
            let action_decode = decode(actionId || "");
            if (action_decode) {
              const invoiceId = action_decode?.split("_")[0];
              await dispatch(
                addNotification({
                  initiated_by: "contact",
                  event_type: "invoice",
                  userid: userData?.data?.accountData?.leadData?.user_id,
                  email: "",
                  event_status: "intent_created",
                  activity_type: "payment_activity",
                  clusterid: clusterid,
                  communication_status: "",
                  accounts_id: userData?.data?.accountData?.id,
                  payment_amount: payload?.total_amount,
                  invoice_id: invoiceId,
                  comments: `Payment of ${payload?.total_amount} for invoice ${invoiceId} was created`,
                })
              );
            }
          }

          if (isRecurring) {
            await handleCardSetup({
              clientSecret,
              orderId: getIntentOrderId(res),
              principalAmount,
            });
          } else {
            await handleCardPayment({
              clientSecret,
              orderId: getIntentOrderId(res),
              principalAmount,
            });
          }
        } else {
          setIsErr(res?.body?.data?.message || PAYMENT_START_ERROR);
          await dispatch(onSetShowDisclosure(false));
          await dispatch(onSetFinalDisclosureLoading(false));
          dispatch(setIsPaymentProcessing(false));
        }
      } catch (error) {
        setIsErr(PAYMENT_START_ERROR);
        captureException(error, {
          tags: { payment_flow: "stripe_card_intent" },
          extra: { is_retry: isRetry, is_recurring: isRecurring },
        });
        await dispatch(onSetShowDisclosure(false));
        await dispatch(onSetFinalDisclosureLoading(false));
        dispatch(setIsPaymentProcessing(false));
      } finally {
        setIsLoading(false);
        dispatch(setIsPaymentProcessing(false));
      }
    };

    const handleRetryNextStore = () => {
      if (!stripe || !elements) {
        return;
      }

      const cardElement = elements.getElement(CardNumberElement);

      if (!cardElement) return;

      setStoredCardElement(cardElement);
      handleRetryNext();
    };

    useImperativeHandle(ref, () => ({
      callHandleClick: () => handleOnSubmit(),
    }));

    const amountLimit = useMemo(() => {
      if (
        userData?.data?.accountData?.balance &&
        generalSettings?.systemSettings
      ) {
        const { min, max } = formatMinAndMaxAmount(
          generalSettings?.systemSettings,
          userData?.data?.accountData?.balance
        );

        return { min, max };
      }

      return { min: 1.0, max: 100000.0 };
    }, [generalSettings, userData]);

    let amountCheck = useMemo(() => {
      if (isDowngradePlan || isUpdateSubscriptionPaymentMethod) {
        return false;
      }
      return amount < amountLimit.min || amount > amountLimit.max;
    }, [
      amount,
      amountLimit,
      isDowngradePlan,
      isUpdateSubscriptionPaymentMethod,
    ]);

    return (
      <form>
        {/* Card details */}

        <div className="flex flex-col gap-5 items-start">
          <div className="w-full">
            <h2 className="text-txtSecondary text-xs font-bold uppercase mb-1">
              CARD INFORMATION
            </h2>
            <div className="w-full border border-strokePrimary rounded-[5px] text-txtPrimary">
              <div className="w-full hover:!border-strokeActive border-b border-strokePrimary py-2 px-4 text-xs relative">
                <CardNumberElement
                  options={cardNumberElementOptions}
                  onBlur={validateStripeCard}
                  onChange={(event) => {
                    dispatch(setIsPaymentProcessing(true));
                    if (event.brand !== "unknown") {
                      setIconType(event.brand);
                    }
                    if (event.empty && !formCheck.expiry && !formCheck.cvv) {
                      setCardError(null);
                    }
                    setFormEmptyCheck((prev) => ({
                      ...prev,
                      card: event.empty,
                    }));
                    setFormCheck((prev) => ({ ...prev, card: event.complete }));
                  }}
                />
                {iconType?.length > 0 && (
                  <div
                    className={`absolute  right-4 top-1/2 transform -translate-y-1/2 text-xs capitalize ${
                      iconType ? "w-35px" : "w-174px"
                    }`}
                  >
                    {imgUrl()}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 w-full">
                <div className="border-r hover:!border-strokeActive border-strokePrimary py-2 px-4 text-xs">
                  <CardExpiryElement
                    options={elementOptions}
                    onBlur={validateStripeCard}
                    onChange={(event) => {
                      dispatch(setIsPaymentProcessing(true));
                      if (event.empty && !formCheck.card && !formCheck.cvv) {
                        setCardError(null);
                      }
                      setFormEmptyCheck((prev) => ({
                        ...prev,
                        expiry: event.empty,
                      }));
                      setFormCheck((prev) => ({
                        ...prev,
                        expiry: event.complete,
                      }));
                    }}
                  />
                </div>
                <div className="py-2 hover:!border-strokeActive px-4 text-xs">
                  <CardCvcElement
                    options={elementOptions}
                    onBlur={validateStripeCard}
                    onChange={(event) => {
                      dispatch(setIsPaymentProcessing(true));
                      if (event.empty && !formCheck.expiry && !formCheck.card) {
                        setCardError(null);
                      }
                      setFormEmptyCheck((prev) => ({
                        ...prev,
                        cvv: event.empty,
                      }));
                      setFormCheck((prev) => ({
                        ...prev,
                        cvv: event.complete,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          {cardError && <div className="text-xs text-red">{cardError}</div>}
          <div className="w-full">
            <InputField
              name="choldername"
              label="Cardholder name"
              className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
              _className="!text-xs !font-bold !leading-normal"
              placeholder="Full name on card"
              value={values?.choldername}
              onChange={(e) => handleChange(e, "choldername")}
              onBlur={() => {
                validateStripeCard({ elementType: "choldername" });
              }}
              tabInd={4}
            />
          </div>

          {portalSettings?.cc_billing_address === SettingsValue.YES &&
            collectAddress && (
              <div className="w-full">
                <InputSelect
                  label="Billing Address"
                  className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
                  _className="!text-xs !font-bold !leading-normal"
                  optionClassName="!text-xs"
                  arrowClassName="!right-[11px] !top-[10px]"
                  placeholder="Country"
                  selected={selectedCountryUI}
                  options={countryList}
                  setSelected={(e: any) => {
                    autoDetectRef.current = true;
                    const nStates = getStatesForCountryCode(e?.value as string);
                    setStates(nStates);
                    setSelectedState(undefined);
                    handleChange({ target: { value: "" } }, "x_state");
                    handleChange(e, "x_country");
                  }}
                  tabInd={5}
                />
                {fieldArr && fieldArr.includes("Address line 1") && (
                  <InputField
                    name="x_address"
                    showLabel={false}
                    className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                      (fieldArr &&
                        (fieldArr.includes("City") ||
                          fieldArr.includes("Area") ||
                          fieldArr.includes("Address line 2"))) ||
                      getPostalCode() ||
                      getStateName()
                        ? "!rounded-none"
                        : "!rounded-b-[5px] !rounded-t-none"
                    }`}
                    _className="!text-xs !font-bold !leading-normal"
                    placeholder="Address line 1"
                    value={values?.x_address}
                    onChange={(e) => handleChange(e, "x_address")}
                    tabInd={6}
                  />
                )}
                {fieldArr && fieldArr.includes("Address line 2") && (
                  <InputField
                    name="address2"
                    showLabel={false}
                    className={`!pl-[15px] !pr-[15px] !py-2 !text-xs  !border-t-0 ${
                      (fieldArr &&
                        (fieldArr.includes("City") ||
                          fieldArr.includes("Area"))) ||
                      getPostalCode() ||
                      getStateName()
                        ? "!rounded-none"
                        : "!rounded-b-[5px] !rounded-t-none"
                    }`}
                    _className="!text-xs !font-bold !leading-normal"
                    placeholder="Address line 2"
                    value={values?.address2}
                    onChange={(e) => handleChange(e, "address2")}
                    tabInd={7}
                  />
                )}
                <div
                  className={`grid ${
                    getPostalCode()
                      ? !fieldArr?.includes("City") &&
                        !fieldArr?.includes("Area")
                        ? "grid-cols-1"
                        : "grid-cols-2"
                      : "grid-cols-1"
                  }`}
                >
                  {fieldArr && fieldArr.includes("City") && (
                    <InputField
                      name="x_city"
                      showLabel={false}
                      className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                        getStateName()
                          ? "!rounded-none"
                          : getPostalCode()
                            ? "rounded-b-[5px] !rounded-r-none"
                            : "!rounded-[5px] !rounded-t-none"
                      }`}
                      _className="!text-xs !font-bold !leading-normal"
                      placeholder="City"
                      value={values?.x_city}
                      onChange={(e) => handleChange(e, "x_city")}
                      tabInd={8}
                    />
                  )}
                  {fieldArr && fieldArr.includes("Area") && (
                    <InputField
                      name="x_city"
                      showLabel={false}
                      className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                        getStateName()
                          ? "!rounded-none"
                          : getPostalCode()
                            ? "rounded-b-[5px] !rounded-r-none"
                            : "!rounded-[5px] !rounded-t-none"
                      }`}
                      _className="!text-xs !font-bold !leading-normal"
                      placeholder="Area"
                      value={values?.x_city}
                      onChange={(e) => handleChange(e, "x_city")}
                      tabInd={8}
                    />
                  )}

                  {getPostalCode() && (
                    <InputField
                      name="x_zip"
                      type={fieldObj?.isNumber ? "number" : "text"}
                      showLabel={false}
                      className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                        !fieldArr?.includes("City") &&
                        !fieldArr?.includes("Area")
                          ? ""
                          : "!border-l-0"
                      } ${
                        getStateName()
                          ? "!rounded-none"
                          : "!rounded-b-[5px] !rounded-l-none !rounded-t-none"
                      }`}
                      _className="!text-xs !font-bold !leading-normal"
                      placeholder={getPostalCode() || ""}
                      value={values?.x_zip}
                      onChange={(e) => {
                        if (
                          e.target.value?.length <= (fieldObj?.zipLength || 50)
                        )
                          handleChange(e, "x_zip");
                      }}
                      tabInd={9}
                    />
                  )}
                </div>
                {states && states.length > 0 && (
                  <InputSelect
                    showLabel={false}
                    className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none !border-t-0 "
                    _className="!text-xs !font-bold !leading-normal"
                    optionClassName="!text-xs"
                    placeholder={getStateName() || "State"}
                    selected={selectedState}
                    arrowClassName="!right-[11px] !top-[10px]"
                    isManual
                    options={states}
                    setSelected={(e: any) => {
                      if (e?.value) {
                        setSelectedState(e);
                        handleChange(e, "x_state");
                      }
                    }}
                    tabInd={10}
                  />
                )}
              </div>
            )}

          {(portalSettings?.cc_billing_address === SettingsValue.NO ||
            !collectAddress) && (
            <div className="w-full">
              <InputSelect
                label="Billing Address"
                className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
                _className="!text-xs !font-bold !leading-normal"
                optionClassName="!text-xs"
                arrowClassName="!right-[11px] !top-[10px]"
                placeholder="Country"
                selected={selectedCountryUI}
                options={countryList}
                setSelected={(e: any) => {
                  autoDetectRef.current = true;
                  const nStates = getStatesForCountryCode(e?.value as string);
                  setStates(nStates);
                  setSelectedState(undefined);
                  handleChange({ target: { value: "" } }, "x_state");
                  handleChange(e, "x_country");
                }}
                tabInd={5}
              />
              {getPostalCode() && (
                <InputField
                  name="x_zip"
                  type={fieldObj?.isNumber ? "number" : "text"}
                  showLabel={false}
                  className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-b-[5px] !border-t-0 !rounded-t-none "
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder={getPostalCode() || ""}
                  value={values?.x_zip}
                  onChange={(e) => {
                    if (e.target.value?.length <= (fieldObj?.zipLength || 50))
                      handleChange(e, "x_zip");
                  }}
                  tabInd={9}
                />
              )}
            </div>
          )}
        </div>

        <div>
          {showConvFee &&
            portalSettings?.convenience_fee_opt !== SettingsValue.NO && (
              <ConvenienceFeeDisplay
                conFee={conFee}
                isFeeLoading={isFeeLoading}
                isPaymentLink={isPaymentLink}
                setShowPreview={setShowPreview}
              />
            )}

          {isErr || cardErrMsg ? (
            <div className="text-xs text-red pt-3">{isErr || cardErrMsg}</div>
          ) : null}
        </div>

        <div className="relative w-full pt-5">
          <InputField
            name="Email"
            label="Email"
            className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
            _className="!text-xs !font-bold !leading-normal"
            placeholder="Email address"
            onBlur={handleEmailBlur}
            onChange={(e) => {
              setEmail(
                userData?.data?.accountData?.email
                  ? userData?.data?.accountData?.email
                  : e.target.value
              );
            }}
            value={
              userData?.data?.accountData?.email
                ? userData?.data?.accountData?.email
                : email
            }
            disabled={userData?.data?.accountData?.email}
          />
        </div>

        {collectPhone && (
          <div className="pt-5">
            <div
              className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
            >
              Phone
            </div>
            <MobileInputNew
              inputValue={phoneNumber?.phone || ""}
              isProductLinkInput={true}
              userCountry={
                getCountryShortName(phoneNumber?.country_code) || "US"
              }
              setCountry={handlePhoneCountryChange}
              setInputValue={handleOnPhoneChange}
            />
          </div>
        )}

        {!isRetry && (
          <div className="my-5">
            {isAgreement ? (
              <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
                <Button
                  text={`Back`}
                  variant="white"
                  onClick={handleBack}
                  isDisabled={isLoading}
                  className="lg:w-40 w-full max-w-40"
                />
                <Button
                  text={`Confirm`}
                  isDisabled={isDisabled || amountCheck}
                  isLoading={isLoading}
                  onClick={() => {
                    handleOnSubmit();
                  }}
                  className="lg:w-40 w-full max-w-40"
                  variant="variable"
                />
              </div>
            ) : isSchedulePayment ? (
              <div className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5">
                <div
                  className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
                >
                  {isUpdatePlan && (
                    <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
                  )}

                  <div className="w-full flex justify-between gap-5">
                    <Button
                      className="lg:!w-40 !w-full"
                      isDisabled={isLoading}
                      text="Back"
                      type="button"
                      variant="secondary"
                      onClick={handleBack}
                    />
                    <Button
                      className="lg:!w-40 !w-full"
                      isLoading={isLoading}
                      isDisabled={isDisabled || amountCheck}
                      text={isUpdatePlan ? "Update" : "Confirm"}
                      type="button"
                      onClick={() => {
                        handleOnSubmit();
                      }}
                      variant="variable"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <Button
                text={
                  isUpdatePlan || isUpdateSubscription
                    ? "Update"
                    : `Pay ${
                        formatCurrency(
                          getPaymentTotalWithFee(amount, conFee)
                        ) || 0.0
                      }`
                }
                className={"!w-full mt-5"}
                isDisabled={
                  isDisabled ||
                  amountCheck ||
                  (isProductLink && !isSubscriptionAllowed)
                }
                onClick={() => {
                  handleOnSubmit();
                }}
                isLoading={isLoading || isFeeLoading || isChecking}
                variant={"variable"}
              />
            )}
          </div>
        )}

        {isRetry && (
          <div className="w-full flex justify-end pt-8">
            <Button
              text="Next"
              className="!px-14"
              isDisabled={isDisabled || isFeeLoading}
              isLoading={isFeeLoading}
              variant="variable"
              onClick={() => {
                handleRetryNextStore();
              }}
            />
          </div>
        )}
      </form>
    );
  }
);

export default StripeCardPaymentCheckoutForm;
